// components/TableStyles.js
import styled from 'styled-components';

export const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--accent-secondary) 0%, var(--accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin: 0 0 1rem 0;
  text-shadow: var(--title-shadow);
  text-align: center;
  flex-shrink: 0;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin: 0 0 0.75rem 0;
  }
`;

export const HeaderSection = styled.header`
  text-align: center;
  position: relative;
  width: 100%;
  flex-shrink: 0;
`;

export const Wrapper = styled.div`
  width: 100%;
  max-width: 100%;
  flex: 1 1 auto;
  min-height: min-content;
  height: auto;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: var(--surface-glass);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
  border: 1px solid var(--border-default);
  border-radius: 20px;
  padding: 1rem 1.25rem;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  overflow: visible;

  @media screen and (max-width: 768px) {
    padding: 0.75rem;
    border-radius: 16px;
  }
`;

/** Tabela cresce com o conteúdo — scroll fica na página (SideContainer). */
export const TableScroll = styled.div`
  flex: 0 0 auto;
  width: 100%;
  height: auto;
  max-height: none;
  overflow-x: auto;
  overflow-y: visible;
  border-radius: 12px;

  &::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: var(--scrollbar-track);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--accent);
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--accent-hover);
  }
`;

export const FilterButton = styled.button`
  padding: 6px 14px;
  border: 1px solid ${props => props.$active ? 'var(--border-subtle)' : 'var(--border-subtle)'};
  
  background: ${props => {
    if (props.$active) {
      return 'var(--brand-lime)';
    }
    return 'var(--surface-1)';
  }};
  
  color: ${props => {
    if (props.$active) {
      return 'var(--text-on-lime)';
    }
    return 'var(--text-secondary)';
  }};
  
  border-radius: 20px;
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: ${props => props.$active ? 'none' : 'blur(4px)'};
  
  &:hover {
    background: ${props => props.$active ? 'var(--brand-lime)' : 'var(--surface-hover)'};
    border-color: ${props => props.$active ? 'var(--border-strong)' : 'var(--border-strong)'};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
    border-color: var(--border-subtle);
     background: ${props => props.$active ? 'var(--brand-lime)' : 'var(--surface-hover)'};
    color: var(--text-on-lime);
  }
`;

export const ReadStatusFilter = styled(FilterButton)`
  border-color: ${props => {
    if (props.$active) return 'var(--border-subtle)';
    return 'var(--border-default)';
  }};

  background: ${props => props.$active ? 'var(--brand-lime)' : 'var(--surface-2)'};

  color: ${props => {
    if (props.$active) {
      return 'var(--text-on-lime)';
    }
    return 'var(--text-secondary)';
  }};

  &:hover {
    background: ${props => props.$active ? 'var(--brand-lime)' : 'var(--surface-hover)'};
    border-color: ${props => props.$active ? 'var(--border-strong)' : 'var(--border-strong)'};
  }

  &:active {
    transform: translateY(0px);
    border-color: var(--border-subtle);
    background: ${props => props.$active ? 'var(--brand-lime)' : 'var(--surface-hover)'};
    color: var(--text-on-lime);
  }
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 14px;
  border-radius: 12px;
`;

export const Thead = styled.thead`
  border-bottom: var(--table-header-border);
  background: var(--table-header-bg);
  backdrop-filter: blur(10px);
  position: sticky;
  top: 0;
  z-index: 10;
  
  th:first-child {
    border-top-left-radius: 12px;
  }
  
  th:last-child {
    border-top-right-radius: 12px;
  }
`;

export const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-weight: var(--table-header-font-weight);
  color: var(--table-header-color);
  font-size: var(--table-header-font-size);
  text-transform: uppercase;
  letter-spacing: var(--table-header-letter-spacing);
`;

export const Td = styled.td`
  padding: 12px 16px;
  vertical-align: middle;
  color: var(--text-secondary);
`;

export const DescriptionCell = styled(Td)`
  max-width: 420px;
  vertical-align: top;
  font-size: 0.875rem;
  line-height: 1.45;
  word-break: break-word;

  p {
    margin: 0 0 0.35rem;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

export const Row = styled.tr`
  border-bottom: 1px solid var(--border-subtle);
  background: ${props => {
    if (props.$isRead) {
      return 'var(--surface-primary)';
    }
    return 'var(--surface-highlight)';
  }};
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--table-row-hover);
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &:last-child td:first-child {
    border-bottom-left-radius: 12px;
  }
  
  &:last-child td:last-child {
    border-bottom-right-radius: 12px;
  }
`;

export const Badge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  background: ${props => props.$color || 'var(--text-muted)'};
`;

export const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 9999px; /* Formato pílula */
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  
  /* 👇 AQUI ESTÁ O SEGREDO */
  /* Se receber $color, usa ela para borda e texto, e um fundo claro */
  background-color: ${(props) => (props.$color ? `${props.$color}1A` : "transparent")}; /* 1A = 10% de opacidade em Hex */
  color: ${(props) => props.$color || "#374151"};
  border: 1px solid ${(props) => props.$color || "#d1d5db"};
`;

export const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${props => props.$error ? 'var(--danger)' : 'var(--text-muted)'};
  font-size: 16px;
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

export const ReadStatusButton = styled.button`
  padding: 6px 14px;
  border: 1px solid ${props => {
    if (props.$isRead) {
      return 'var(--border-default)';
    }
    return 'var(--border-subtle)';
  }};
  
  background: ${props => {
    if (props.$isRead) {
      return 'var(--surface-2)';
    }
    return 'var(--brand-lime)';
  }};
  
  color: ${props => {
    if (props.$isRead) {
      return 'var(--text-muted)';
    }
    return 'var(--text-on-lime)';
  }};
  
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  backdrop-filter: ${props => props.$isRead ? 'blur(4px)' : 'none'};
  
  &:hover {
    background: ${props => {
    if (props.$isRead) {
      return 'var(--surface-hover)';
    }
    return 'var(--brand-lime)';
  }};
    border-color: ${props => {
    if (props.$isRead) {
      return 'var(--border-strong)';
    }
    return 'var(--border-strong)';
  }};
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0px);
    border-color: var(--border-subtle);
    background: ${props => {
    if (props.$isRead) {
      return 'var(--surface-hover)';
    }
    return 'var(--brand-lime)';
  }};
    color: ${props => {
    if (props.$isRead) {
      return 'var(--text-muted)';
    }
    return 'var(--text-on-lime)';
  }};
  }
`;

export const ViewButton = styled.button`
  padding: 6px 14px;
  border: 1px solid var(--accent);
  background: var(--accent);
  color: var(--text-on-brand);
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  box-shadow: 0 2px 8px var(--accent-soft);
  
  &:hover {
    background: var(--accent-hover);
    border-color: var(--accent-hover);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px var(--accent-soft);
  }

  &:active {
    transform: translateY(0px);
    box-shadow: 0 2px 4px var(--accent-soft);
  }
`;

export const UnreadIndicator = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  background: var(--accent);
  border-radius: 50%;
  margin-left: 8px;
  animation: pulse 2s ease-in-out infinite;
  box-shadow: 0 0 12px var(--accent-soft);
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.6;
      transform: scale(0.85);
    }
  }
`;

export const FiltersWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 1rem;
  padding: 16px;
  background: linear-gradient(
    180deg,
    var(--surface-primary),
    var(--surface-secondary)
  );
  border-radius: 12px;
  border: 1px solid var(--border-default);
  box-shadow: var(--shadow-sm);
  flex-shrink: 0;
`;

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  
  &:not(:last-child) {
    padding-bottom: 12px;
    border-bottom: 1px solid var(--border-subtle);
  }
`;

export const FilterLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--text-heading);
  margin-right: 4px;
  min-width: 80px;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  flex: 1;
`;

export const UnreadCountBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: var(--accent);
  color: var(--text-on-accent);
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  margin-left: 6px;
  min-width: 20px;
  height: 20px;
`;

export const TitleWithCount = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
`;

export const ReadStatusIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${props => props.$isRead ? 'var(--success-soft)' : 'var(--surface-2)'};
  color: ${props => props.$isRead ? 'var(--success)' : 'var(--text-muted)'};
  font-size: 12px;
  margin-right: 4px;
  border: 1px solid ${props => props.$isRead ? 'var(--success)' : 'var(--border-default)'};
`;
/** Desktop table — oculto no mobile */
export const DesktopOnlyTable = styled.div`
  @media screen and (max-width: 768px) {
    display: none;
  }
`;

/** Cards empilhados — só mobile */
export const MobileCards = styled.div`
  display: none;

  @media screen and (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: 100%;
  }
`;

export const MobileCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
  padding: 0.9rem 1rem;
  border-radius: 14px;
  border: 1px solid var(--border-default);
  background: var(--surface-2);
  box-shadow: var(--shadow-sm);
`;

export const MobileCardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
`;

export const MobileCardTitle = styled.h3`
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--text-heading);
  line-height: 1.35;
`;

export const MobileCardMeta = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: var(--text-secondary);
  line-height: 1.4;
  word-break: break-word;

  p {
    margin: 0 0 0.35rem;
  }

  p:last-child {
    margin-bottom: 0;
  }
`;

export const MobileCardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.25rem;

  button {
    flex: 1 1 auto;
    min-width: 120px;
  }
`;
